import { computed, ref } from "vue"

export function groupArticlesByCategory(articles) {
  const groups = new Map()

  articles.forEach((article) => {
    const category = article.category || ""
    if (!groups.has(category)) groups.set(category, [])
    groups.get(category).push(article)
  })

  return Array.from(groups.entries())
    .map(([category, items]) => ({
      category,
      articles: items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    }))
    .sort((a, b) => (a.articles[0]?.position ?? 0) - (b.articles[0]?.position ?? 0))
}

export function useHelpArticles(articlesUrl, searchUrl) {
  const articles = ref([])
  const isLoading = ref(false)
  const loadError = ref(null)
  const searchQuery = ref("")
  const searchResults = ref(null)
  const isSearching = ref(false)
  const searchError = ref(null)

  const tocGroups = computed(() => groupArticlesByCategory(articles.value))

  async function loadArticles() {
    if (!articlesUrl) return

    isLoading.value = true
    loadError.value = null

    try {
      const response = await fetch(articlesUrl, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      articles.value = await response.json()
    } catch (error) {
      loadError.value = error.message
      articles.value = []
    } finally {
      isLoading.value = false
    }
  }

  async function performSearch(query) {
    if (!searchUrl) return

    const trimmed = query.trim()
    searchQuery.value = trimmed

    if (!trimmed) {
      searchResults.value = null
      return
    }

    isSearching.value = true
    searchError.value = null

    try {
      const url = `${searchUrl}?q=${encodeURIComponent(trimmed)}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const payload = await response.json()
      searchResults.value = payload.results || []
    } catch (error) {
      searchError.value = error.message
      searchResults.value = []
    } finally {
      isSearching.value = false
    }
  }

  function clearSearch() {
    searchQuery.value = ""
    searchResults.value = null
    searchError.value = null
  }

  return {
    articles,
    isLoading,
    loadError,
    searchQuery,
    searchResults,
    isSearching,
    searchError,
    tocGroups,
    loadArticles,
    performSearch,
    clearSearch,
  }
}
